import { cloneDeep, isFunction } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import cookies from 'js-cookie';

import {
  saveAnonymousProfile,
  isFreeWillWeek,
  transformSeomaticMeta,
  cloneMetaFromCraftSeomatic,
} from '~/util';

import GET_PARTNERSHIP_QUERY from '~/graphql/queries/GetPartnership';
import GET_SEOMATIC_META_QUERY from '~/graphql/queries/GetSeomaticMeta';

import { partnerships } from '~/vanityUrls.json';

export default ({
  query,
  isLocalizable = false,
  transform,
  variables = () => {
    return {};
  },
  customUri,
  keepLocale = true,
}) => {
  return {
    head() {
      if (this.seomatic && Array.isArray(this.seomatic.meta)) {
        // Meta from Craft can have either "name" or "property"
        // { content: "foobar", name: 'description' } or
        // { content: 'Safewill', property: 'og:site_name' }
        this.seomatic.meta.forEach((meta) => {
          meta.hid = meta.property || meta.name;
        });

        // Override default Nuxt's meta with meta from Seomatic
        this.seomatic.meta = this.seomatic.meta.concat([
          ...cloneMetaFromCraftSeomatic(
            this.seomatic.meta,
            'og:image',
            'twitter:image'
          ),
          ...cloneMetaFromCraftSeomatic(
            this.seomatic.meta,
            'description',
            'twitter:description'
          ),
          ...cloneMetaFromCraftSeomatic(
            this.seomatic.meta,
            'og:title',
            'twitter:title'
          ),
          ...cloneMetaFromCraftSeomatic(
            this.seomatic.meta,
            'og:url',
            'twitter:url'
          ),
        ]);
      }
      return this.seomatic;
    },
    async asyncData(context) {
      const { app, error, route } = context;
      const routeName = route.name?.split('___')[0];
      const countryCode = app.i18n.locale.toUpperCase();

      const partnershipUrl = partnerships.find(
        (p) => p.slug === routeName && p.country.includes(countryCode)
      );
      const partnershipSlug = partnershipUrl?.slug;
      let seomatic = {};
      let craft = {};

      try {
        if (app.apolloProvider.defaultClient) {
          let uri = customUri ?? route.fullPath;

          if (keepLocale) {
            // convert /nz/about-us to /nz-about-us
            uri = uri.replace(/^\/(nz|au)\//, '/$1-');
          } else {
            uri = uri.replace(/^\/(nz|au)/, '');
          }

          if (query) {
            const craftQuery = await app.apolloProvider.defaultClient.query({
              query,
              variables: {
                ...variables(context),
                ...(isLocalizable
                  ? {
                      country: countryCode,
                    }
                  : {}),
              },
            });

            craft = isFunction(transform)
              ? transform({
                  ...cloneDeep(craftQuery.data),
                  app,
                })
              : craftQuery.data;

            if (partnershipSlug) {
              const partnershipQuery =
                await app.apolloProvider.defaultClient.query({
                  query: GET_PARTNERSHIP_QUERY,
                  variables: {
                    id: partnershipSlug,
                  },
                });

              if (partnershipQuery.data) {
                // replace partnership uri vanity slug with the actual slug
                uri = uri.replace(
                  partnershipSlug,
                  partnershipQuery.data.entry.slug
                );

                craft = {
                  ...craft,
                  partner: { ...partnershipQuery.data.entry, partnershipSlug },
                };
              }
            }
          }

          const { data } = await app.apolloProvider.defaultClient.query({
            query: GET_SEOMATIC_META_QUERY,
            variables: {
              uri,
            },
          });

          seomatic = transformSeomaticMeta(data);
        }
      } catch ({ message, status }) {
        return error({
          statusCode: status,
          message,
        });
      }
      return { ...craft, seomatic };
    },
    created() {
      const partnership = this.partner;
      if (partnership) {
        cookies.set('partner', partnership.partnershipSlug);
        this.setPartnership(partnership);

        const overideBySaleBanner =
          this.entry?.banner?.[0]?.bannerEnabled &&
          this.entry?.banner?.[0]?.overrideCharityBanner;
        if (!overideBySaleBanner) {
          this.setBannerLogo(partnership.bannerLogo);
          this.setBannerName(partnership.name);
          this.setBannerText(
            isFreeWillWeek() && partnership.fwwBannerText
              ? partnership.fwwBannerText
              : partnership.bannerText
          );
        }

        this.setAppQuery(partnership.appQueryParams);
        this.setTheme(partnership.themeColours);
        const couponCode = partnership.appQueryParams?.find(
          (appQuery) => appQuery.key === 'coupon'
        )?.value;
        saveAnonymousProfile({ couponCode });
      }
    },
    computed: {
      ...mapGetters(['partnership']),
    },
    methods: {
      ...mapActions([
        'setTheme',
        'setAppQuery',
        'setPartnership',
        'setAffiliate',
      ]),
      ...mapActions('banner', [
        'setBannerLogo',
        'setBannerText',
        'setBannerName',
      ]),
    },
  };
};
